import { L4_QUALITY, ladderDistance, type L4Quality } from "./marks.js";

/**
 * The MW mock week: an opt-in schedule of three twelve-wine papers on three consecutive local
 * days. It is a schedule and nothing more, so no day is ever "missed", nothing is counted and no
 * streak exists; a day not yet sat is only that.
 */
export interface MockDay {
  /** A local calendar day, `YYYY-MM-DD`. */
  readonly day: string;
  readonly paperId: string;
}

export interface MockWeekSchedule {
  readonly weekId: string;
  readonly days: readonly MockDay[];
  /** Local time of day, `HH:MM`; absent means reminders are off. */
  readonly reminderAt?: string;
  readonly createdAt: string;
  readonly cancelledAt?: string;
  /** Set once the remaining days were moved: the days then need not be consecutive. */
  readonly moved?: true;
}

export const MOCK_WEEK_DAYS = 3;

/** The three twelve-wine papers of the MW track's mock week, one per day. */
export const MW_PAPER_IDS = ["p4-mw-1", "p4-mw-2", "p4-mw-3"] as const;
export const isMw12Paper = (paperId: string): boolean => MW_PAPER_IDS.some((id) => id === paperId);

const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Whether `value` is a real calendar day such as `2026-10-26`. */
export function isLocalDay(value: string): boolean {
  const match = DAY.exec(value);
  if (match === null) return false;
  const [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const at = new Date(Date.UTC(y, m - 1, d));
  return at.getUTCFullYear() === y && at.getUTCMonth() === m - 1 && at.getUTCDate() === d;
}

export function addDays(day: string, days: number): string {
  const match = DAY.exec(day);
  const at = new Date(
    Date.UTC(Number(match?.[1]), Number(match?.[2]) - 1, Number(match?.[3]) + days),
  );
  return at.toISOString().slice(0, 10);
}

export type ScheduleResult =
  | { readonly ok: true; readonly schedule: MockWeekSchedule }
  | { readonly ok: false; readonly reason: "past" | "invalid" };

/** The id of a week is its first day, so two weeks starting on one day are one week. */
export const mockWeekId = (startDay: string): string => `mw-${startDay}`;

export function scheduleMockWeek(input: {
  readonly startDay: string;
  readonly today: string;
  readonly paperIds: readonly string[];
  readonly now: string;
  readonly reminderAt?: string;
}): ScheduleResult {
  if (!isLocalDay(input.startDay) || !isLocalDay(input.today))
    return { ok: false, reason: "invalid" };
  if (input.startDay < input.today) return { ok: false, reason: "past" };
  if (input.paperIds.length !== MOCK_WEEK_DAYS) return { ok: false, reason: "invalid" };
  if (input.reminderAt !== undefined && !TIME.test(input.reminderAt)) {
    return { ok: false, reason: "invalid" };
  }
  return {
    ok: true,
    schedule: {
      weekId: mockWeekId(input.startDay),
      days: input.paperIds.map((paperId, i) => ({ day: addDays(input.startDay, i), paperId })),
      ...(input.reminderAt === undefined ? {} : { reminderAt: input.reminderAt }),
      createdAt: input.now,
    },
  };
}

/** The shape rules the edge and the app share; `isMw12` says whether a paper id is of the mw-12 shape. */
export function validateMockWeek(
  schedule: MockWeekSchedule,
  isMw12: (paperId: string) => boolean,
): boolean {
  const { days } = schedule;
  if (days.length !== MOCK_WEEK_DAYS) return false;
  if (!days.every((d) => isLocalDay(d.day) && isMw12(d.paperId))) return false;
  if (new Set(days.map((d) => d.paperId)).size !== MOCK_WEEK_DAYS) return false;
  if (schedule.reminderAt !== undefined && !TIME.test(schedule.reminderAt)) return false;
  if (schedule.moved === true) return new Set(days.map((d) => d.day)).size === MOCK_WEEK_DAYS;
  return days.every((d, i) => d.day === addDays(days[0]?.day ?? "", i));
}

export type DayStatus = "sat" | "today" | "upcoming" | "open";

/** A past day without a sitting is `open`: it can be sat now or moved, and it costs nothing. */
export function dayStatus(
  schedule: MockWeekSchedule,
  dayIndex: number,
  today: string,
  sat: readonly { readonly dayIndex: number }[],
): DayStatus {
  if (sat.some((s) => s.dayIndex === dayIndex)) return "sat";
  const day = schedule.days[dayIndex - 1]?.day;
  if (day === undefined || day < today) return "open";
  return day === today ? "today" : "upcoming";
}

/** The days not yet sat start again on `startDay`, one after another; sat days keep theirs. */
export function moveRemainingDays(
  schedule: MockWeekSchedule,
  input: { readonly sat: readonly number[]; readonly startDay: string; readonly today: string },
): ScheduleResult {
  if (!isLocalDay(input.startDay)) return { ok: false, reason: "invalid" };
  if (input.startDay < input.today) return { ok: false, reason: "past" };
  let step = 0;
  const days = schedule.days.map((d, i) => {
    if (input.sat.includes(i + 1)) return d;
    const moved = { ...d, day: addDays(input.startDay, step) };
    step += 1;
    return moved;
  });
  return { ok: true, schedule: { ...schedule, days, moved: true } };
}

export const cancelMockWeek = (schedule: MockWeekSchedule, now: string): MockWeekSchedule => ({
  ...schedule,
  cancelledAt: now,
});

// ---- badges --------------------------------------------------------------------------

/**
 * The one table of badges, for mock-week milestones only: celebratory, never a prediction, never
 * for wines or bottles, never ranked and never taken back.
 */
export const BADGE_TABLE = [
  { id: "mock-day-1", rule: "the paper of day 1 is marked on the device" },
  { id: "mock-day-2", rule: "the paper of day 2 is marked on the device" },
  { id: "mock-day-3", rule: "the paper of day 3 is marked on the device" },
  { id: "mock-week-complete", rule: "all three papers of the mock week are marked" },
  {
    id: "mock-week-calibrated",
    rule: "the week is complete and the mean absolute quality-ladder distance over its 36 calls is at most 1 step",
  },
] as const;

export type BadgeId = (typeof BADGE_TABLE)[number]["id"];
export const BADGE_IDS: readonly BadgeId[] = BADGE_TABLE.map((b) => b.id);
export const isBadgeId = (value: unknown): value is BadgeId =>
  typeof value === "string" && (BADGE_IDS as readonly string[]).includes(value);

/** The calibrated rule: the mean distance, in ladder steps, over all 36 calls of the week. */
export const CALIBRATED_MAX_DISTANCE = 1;
export const CALIBRATED_CALLS = 36;

export interface Badge {
  readonly id: BadgeId;
  readonly weekId: string;
  readonly awardedAt: string;
}

export interface QualityCall {
  /** Absent when the learner left the quality of a wine unanswered. */
  readonly called?: L4Quality;
  readonly key: L4Quality;
}

/** A sitting of a mock-week day: it counts once marked on the device and never when abandoned. */
export interface MockSitting {
  readonly weekId: string;
  readonly dayIndex: 1 | 2 | 3;
  readonly paperId: string;
  readonly markedAt?: string;
  readonly abandoned?: boolean;
  readonly quality: readonly QualityCall[];
}

const distance = (call: QualityCall): number | undefined =>
  call.called === undefined ? undefined : ladderDistance(L4_QUALITY, call.called, call.key);

/** The mean absolute ladder distance of the calls that were made; infinite when there were none. */
export function meanQualityDistance(sittings: readonly MockSitting[]): number {
  const gaps = sittings
    .flatMap((s) => s.quality.map(distance))
    .filter((gap): gap is number => gap !== undefined);
  return gaps.length === 0
    ? Number.POSITIVE_INFINITY
    : gaps.reduce((sum, gap) => sum + gap, 0) / gaps.length;
}

const DAY_BADGES: readonly (readonly [number, BadgeId])[] = [
  [1, "mock-day-1"],
  [2, "mock-day-2"],
  [3, "mock-day-3"],
];

const counts = (s: MockSitting): boolean => s.markedAt !== undefined && s.abandoned !== true;

/**
 * The badges a week's sittings newly earn, given those already held. The marks that count are the
 * deterministic ones, so a pending marker never delays a badge; nothing already held is returned,
 * and nothing is ever removed.
 */
export function awardBadges(
  weekId: string,
  sittings: readonly MockSitting[],
  held: readonly Badge[],
): Badge[] {
  const owned = new Set(held.filter((b) => b.weekId === weekId).map((b) => b.id));
  const byDay = new Map<number, MockSitting>();
  for (const s of sittings.filter((x) => x.weekId === weekId && counts(x))) {
    const earlier = byDay.get(s.dayIndex);
    if (earlier === undefined || (s.markedAt ?? "") < (earlier.markedAt ?? "")) {
      byDay.set(s.dayIndex, s);
    }
  }
  const earned: Badge[] = [];
  const add = (id: BadgeId, awardedAt: string): void => {
    if (!owned.has(id)) earned.push({ id, weekId, awardedAt });
  };
  for (const [index, id] of DAY_BADGES) {
    const at = byDay.get(index)?.markedAt;
    if (at !== undefined) add(id, at);
  }
  if (byDay.size === MOCK_WEEK_DAYS) {
    const week = [...byDay.values()];
    const done =
      week
        .map((s) => s.markedAt ?? "")
        .toSorted()
        .at(-1) ?? "";
    add("mock-week-complete", done);
    const calls = week.flatMap((s) => s.quality).filter((c) => c.called !== undefined);
    if (calls.length === CALIBRATED_CALLS && meanQualityDistance(week) <= CALIBRATED_MAX_DISTANCE) {
      add("mock-week-calibrated", done);
    }
  }
  return earned;
}

/** Devices merge badges by union; the earliest award time wins, so a replay changes nothing. */
export function mergeBadges(a: readonly Badge[], b: readonly Badge[]): Badge[] {
  const merged = new Map<string, Badge>();
  for (const badge of [...a, ...b]) {
    const key = `${badge.weekId}\u0000${badge.id}`;
    const held = merged.get(key);
    if (held === undefined || badge.awardedAt < held.awardedAt) merged.set(key, badge);
  }
  return [...merged.values()].toSorted(
    (x, y) => x.weekId.localeCompare(y.weekId) || BADGE_IDS.indexOf(x.id) - BADGE_IDS.indexOf(y.id),
  );
}
